import Cookies from 'universal-cookie';

import { createChatSession, completeChatSession } from './api';

const cookies = new Cookies();

// Delay before showing a prebaked bot reply, to imitate a real person typing.
export const BOT_REPLY_DELAY = 1000;
export const CONTINUE_TOUR_BUTTON_TEXT = 'Or, Continue To Next Tour Stop';

// Schedule a bot reply, showing the "AI is thinking" indicator during the
// delay. If `startTime` is provided, the delay is reduced by however much
// real work has already elapsed since then.
export const scheduleBotReply = (dispatch, fn, startTime = Date.now()) => {
  const elapsed = Date.now() - startTime;
  const remaining = Math.max(0, BOT_REPLY_DELAY - elapsed);
  if (remaining === 0) {
    fn();
    return;
  }
  dispatch({
    type: 'UPDATE_LAST_MESSAGE',
    payload: { nextDelayMs: remaining, nextDelayDeadline: Date.now() + remaining },
  });
  dispatch({ type: 'SET_IS_AI_THINKING', payload: true });
  setTimeout(() => {
    dispatch({ type: 'UPDATE_LAST_MESSAGE', payload: { nextDelayDeadline: 0 } });
    dispatch({ type: 'SET_IS_AI_THINKING', payload: false });
    fn();
  }, remaining);
};

// Conversation history sent to the AI for follow-up questions.
// Keep last MAX_PAIRS user+bot text exchanges; skip messages with no text
// (button-only flows, system messages). Backend caps to 8 pairs anyway.
const MESSAGE_HISTORY_MAX_PAIRS = 8;
export const buildMessageHistory = ( messages = [] ) => {
  const textMessages = messages
    .filter( ( m ) => ( m.type === 'user' || m.type === 'bot' )
      && typeof m.text === 'string'
      && m.text.trim() !== '' )
    .map( ( m ) => ( {
      role: m.type === 'user' ? 'user' : 'assistant',
      content: m.text,
    } ) );
  return textMessages.slice( -MESSAGE_HISTORY_MAX_PAIRS * 2 );
};

// Formatting
export const formatInterestsList = (interests) => {
  const items = interests.split(', ');
  return items.length > 1
    ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
    : items[0];
};

// More places header variants
const MORE_PLACES_VARIANTS = ['[FIRST_NAME], here are more spots I think would be perfect for you.',
  "[FIRST_NAME], we've got even more places you'll love.",
  '[FIRST_NAME], here are a few more amenities picked just for you.',
  "[FIRST_NAME], there's more to see — here are additional spots I think you'll enjoy."];
let morePlacesIndex = 0;
export const MORE_PLACES_HEADER = () => {
  const variant = MORE_PLACES_VARIANTS[morePlacesIndex % MORE_PLACES_VARIANTS.length];
  morePlacesIndex += 1;
  return variant;
};

// Next step variants
const NEXT_STEP_VARIANTS = ['[FIRST_NAME], what would you like to do next on your [COMMUNITY_NAME] guided tour?',
  "[FIRST_NAME], there's plenty more to explore at [COMMUNITY_NAME]. What do you want to do next?",
  '[FIRST_NAME], what should we do next on your [COMMUNITY_NAME] guided tour?',
  "[FIRST_NAME], we're just getting started! What's next on your [COMMUNITY_NAME] guided tour?",
  "[FIRST_NAME], let's keep your [COMMUNITY_NAME] guided tour going.",
  '[FIRST_NAME], your [COMMUNITY_NAME] guided tour has more in store! What would you like to do next?'];
let nextStepIndex = 0;
export const NEXT_STEP_MESSAGE = () => {
  const variant = NEXT_STEP_VARIANTS[nextStepIndex % NEXT_STEP_VARIANTS.length];
  nextStepIndex += 1;
  return variant;
};

// Share interest variants
const SHARE_INTEREST_VARIANTS = ['Great! Share another interest so I can plan where to take you next at [COMMUNITY_NAME]. For example, a favorite weekend activity?',
  "Awesome! Tell me another interest and I'll pick another spot to show you around [COMMUNITY_NAME]. For example, a hobby you love?",
  'Love it! What else are you into so I can find another place you might love at [COMMUNITY_NAME]? For example, a go-to pastime?',
  "Let's keep going! Share another interest to help me curate the rest of your [COMMUNITY_NAME] tour. For example, something new you'd like to try?",
  'On it! Give me another interest so I can line up the next stop on your [COMMUNITY_NAME] tour. For example, a way you like to spend an afternoon?',
  'Sounds good! What else interests you so I can map out more of [COMMUNITY_NAME] for you? For example, a way you like to relax?',
  "Perfect! Drop another interest and I'll uncover more [COMMUNITY_NAME] spots you'll enjoy. For example, a topic you love?"];
let shareInterestIndex = 0;
export const SHARE_INTEREST_MESSAGE = () => {
  const variant = SHARE_INTEREST_VARIANTS[shareInterestIndex % SHARE_INTEREST_VARIANTS.length];
  shareInterestIndex += 1;
  return variant;
};

// Ask a question variants
const ASK_QUESTION_VARIANTS = ['Ask me anything about [COMMUNITY_NAME] or the area around us. For example, "What dining options are here?" or "Any good coffee shops nearby?"',
  'Curious about [COMMUNITY_NAME] or nearby areas? You could ask "What\'s the closest hospital?" or "Tell me about your fitness classes."',
  'Got questions about [COMMUNITY_NAME] or the area? Try something like "How far is the airport?" or "What wellness amenities do you offer?"',
  'Want to know more about [COMMUNITY_NAME] or the nearby area? You might ask "Tell me about the residences." or "What parks are close by?"',
  'Ask away about [COMMUNITY_NAME] or the area! For instance, "What grocery stores are nearby?" or "Tell me about your leadership."'];
let askQuestionIndex = 0;
export const ASK_QUESTION_MESSAGE = () => {
  const variant = ASK_QUESTION_VARIANTS[askQuestionIndex % ASK_QUESTION_VARIANTS.length];
  askQuestionIndex += 1;
  return variant;
};

// The query parameter that says this run is somebody exercising TourGuide AI
// rather than a prospect enquiring. Drupal records the session with is_test set:
// it stays off the lead dashboards and is never pushed to a CRM.
export const TEST_MODE_PARAM = 'tgai_test';

// Kept for the tab, because the parameter is only on the URL the tester opened.
// The tour navigates client-side and the lead form is several steps in, so by
// the time the session is created window.location.search may no longer carry it
// — and a test run that quietly becomes a real lead is the whole failure this
// exists to prevent. sessionStorage, not localStorage: test mode should not
// outlive the tab and follow the next visitor on a shared machine.
export const TEST_MODE_KEY = 'tgai_test_mode';

/**
 * Whether this tab is exercising TourGuide AI rather than capturing a lead.
 *
 * Sticky once seen. Wrapped because sessionStorage throws outright in a browser
 * set to block site data, and a storage error must not take the chatbot down.
 *
 * This function is the authority on TEST_MODE_PARAM and TEST_MODE_KEY, but it
 * is not their only reader: getSessionScope() in ./storage.js re-declares both
 * literals so that the persisted session id is scoped by test mode (#630). It
 * cannot import them — storage.js is read at reducer-init on every tour and must
 * not depend on a symbol that exists only once this branch has landed — so the
 * coupling between the two modules is two strings and nothing more. Renaming
 * either here without renaming it there silently reopens #630: a real prospect
 * resumes a session id already flagged is_test, and the lead reaches neither the
 * dashboards nor the CRM with nothing on screen to say so. Both names are
 * pinned by name and by value in helpers.test.js for exactly that reason.
 *
 * @returns {boolean} True when the session should be created as a test.
 */
export const isTestMode = () => {
  try {
    if (new URLSearchParams(window.location.search).get(TEST_MODE_PARAM) === '1') {
      window.sessionStorage.setItem(TEST_MODE_KEY, '1');
      return true;
    }

    return window.sessionStorage.getItem(TEST_MODE_KEY) === '1';
  } catch (error) {
    // No storage: fall back to the URL alone, which is still right for a tester
    // who never leaves the page they opened.
    try {
      return new URLSearchParams(window.location.search).get(TEST_MODE_PARAM) === '1';
    } catch (parseError) {
      return false;
    }
  }
};

export const createAirtableSession = async (sessionId, userInfo, communityNid) => {
  try {
    const sessionData = {
      sessionId,
      firstName: userInfo.firstName || '',
      lastName: userInfo.lastName || '',
      email: userInfo.email || '',
      phone: userInfo.phone || '',
      communityNid,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      userAgent: navigator.userAgent,
      utmSource: new URLSearchParams(window.location.search).get('utm_source') || '',
      utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
      isTest: isTestMode(),
    };

    const result = await createChatSession(sessionData);

    if (!result.success) {
      console.error('[TourGuideAi] Failed to create Airtable session:', result.error);
    }

    // The server decides whether it honours the flag — on live it refuses
    // unless somebody has opted the environment in. Say so out loud, because a
    // tester who believes they are in test mode when the site said no is
    // exactly the person who then creates the real lead.
    if (sessionData.isTest && result.success && result.isTest !== true) {
      console.warn(
        '[TourGuideAi] Test mode was requested but this site recorded a REAL lead. '
        + 'See $settings[\'tgai_allow_test_sessions\'] on the Drupal site.',
      );
    }

    return result.success;
  } catch (error) {
    console.error('[TourGuideAi] Error creating Airtable session:', error);
    return false;
  }
};

// A contact hash is a sha256 hex digest. Anything else is a server we do not
// recognise, and pasting it into a URL is not worth the doubt.
const CONTACT_HASH_PATTERN = /^[0-9a-f]{64}$/i;

/**
 * Build the lead's brochure URL from the hash the server returned.
 *
 * The brochure lives at /<community slug>/<contact hash> on this same
 * origin — getUrlData() in AppHelpers reads the slug back out of the first
 * path segment, so a root-relative path is all that is needed and the app never
 * has to know its own public host.
 *
 * The hash is deliberately NOT derived here. It is a digest of the prospect's
 * contact details, computed by LeadGroupingService and stored on the session
 * row; the browser holds the inputs but must not be the authority on the value,
 * or a client and a server that disagree produce a link that 404s in a way
 * neither side can see. completeChatSession() returns the stored hash and this
 * only formats it.
 *
 * @param {string} contactHash - Hash from the session-complete response.
 * @param {string} urlSlug - Community slug this tour is running under.
 * @returns {string|null} The brochure path, or null when there is nothing to
 *   link to (no hash because the prospect gave no email or phone, an
 *   unrecognised hash, or no slug).
 */
export const buildBrochureUrl = (contactHash, urlSlug) => {
  if (!contactHash || !urlSlug) return null;
  if (!CONTACT_HASH_PATTERN.test(contactHash)) return null;
  return `/${encodeURIComponent(urlSlug)}/${contactHash.toLowerCase()}`;
};

/**
 * Say why buildBrochureUrl() came back empty for this completion response.
 *
 * The goodbye renders either way, so a lead who got no link is otherwise
 * invisible from the browser; this is the reason logged against it.
 *
 * @param {object|undefined} result - The completeAirtableSession() response.
 * @param {string} urlSlug - Community slug this tour is running under.
 * @returns {string} A one-line reason.
 */
export const describeMissingBrochureLink = (result, urlSlug) => {
  if (!result?.success) return `session completion failed (${result?.error || 'no response'})`;
  if (!urlSlug) return 'no community slug for this tour';
  if (!result.contactHash) return 'completion response carried no contact hash';
  return 'completion response carried an unrecognized contact hash';
};

/**
 * Complete the session in Drupal.
 *
 * Returns the whole response, not just a success flag: the session-end message
 * needs the contactHash that comes back with it.
 */
export const completeAirtableSession = async (sessionId, completionType = 'natural') => {
  try {
    const result = await completeChatSession(sessionId, completionType);

    if (!result.success) {
      console.error('[TourGuideAi] Failed to complete Airtable session:', result.error);
    }

    return result;
  } catch (error) {
    console.error('[TourGuideAi] Error completing Airtable session:', error);
    return { success: false, error: 'Error completing session' };
  }
};

const COMPLETION_ATTEMPTS = 3;
const COMPLETION_RETRY_BASE_DELAY = 500;

/**
 * Complete the session, retrying a failed attempt with backoff.
 *
 * The goodbye's brochure link exists only if completion returns, so one
 * dropped request used to cost the prospect their link for good (#1582).
 * Repeating it is safe: Drupal overwrites the same status, token() returns
 * the session's existing token, and the CRM queue worker's crm_push_status
 * guard drops a second push. Only failures are retried — a success that
 * carries no contactHash is the server's settled answer, not a blip.
 */
export const completeAirtableSessionWithRetry = async (
  sessionId,
  completionType = 'natural',
  { attempts = COMPLETION_ATTEMPTS, baseDelay = COMPLETION_RETRY_BASE_DELAY } = {},
) => {
  let result;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    result = await completeAirtableSession(sessionId, completionType);
    if (result?.success) return result;
    if (attempt < attempts) {
      const delay = baseDelay * 2 ** (attempt - 1);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, delay); });
    }
  }
  return result;
};

// Favorites do not live in TourGuide AI at all: FavoriteButtonsContext keeps
// them in browser cookies, path '/', with a four-hour maxAge, shared by every
// tour that runs in this browser. A tgai session is far shorter than that and
// nothing was clearing them at its edges, so on a shared kiosk the next guest
// opened the tour with the previous guest's amenities already hearted — and
// carried them into their own brochure. That is #1129's shape again: a store
// whose lifetime is wider than the session it belongs to.
//
// Only the `favorites` cookie's `place` bucket is cleared. Residence, floor
// plan and media favorites belong to the counselor-driven personalization flow,
// which owns its own clearing; and for the same reason this is a no-op while a
// resident session is live, since those places are that resident's rather than
// a previous guest's leftovers.
const FAVORITES_COOKIE = 'favorites';
const RESIDENT_COOKIE = 'resident';
const TGAI_FAVORITE_TYPE = 'place';
const FAVORITES_COOKIE_MAX_AGE = 14400;

// Returns true when it actually removed something, so the caller knows whether
// the in-memory favorites need re-syncing from the cookies.
export const clearTgaiFavorites = () => {
  try {
    if (cookies.get(RESIDENT_COOKIE)) return false;

    const cookie = cookies.get(FAVORITES_COOKIE);
    const favorites = cookie?.favorites;
    if (!favorites || !favorites[TGAI_FAVORITE_TYPE]?.length) return false;

    const remaining = { ...favorites };
    delete remaining[TGAI_FAVORITE_TYPE];

    const options = { path: '/', domain: window.location.hostname };
    cookies.remove(FAVORITES_COOKIE, options);
    cookies.set(
      FAVORITES_COOKIE,
      { ...cookie, favorites: remaining },
      { ...options, maxAge: FAVORITES_COOKIE_MAX_AGE },
    );

    return true;
  } catch (error) {
    // Cookies blocked. Nothing was stored to leak in the first place.
    return false;
  }
};
